import { APP_NAME } from '@foosball/shared';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { api } from '../api/client';
import { Button, Card, Field } from '../components/ui';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
    } catch {
      // Deliberately ignored. The API answers 204 whether or not the address is
      // registered; surfacing a client-side failure differently here would
      // reintroduce the account-enumeration signal the endpoint avoids.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-3xl font-bold tracking-tight">Reset password</h1>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{APP_NAME}</p>

      <Card>
        {sent ? (
          <div role="status">
            <p className="text-sm">
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
            </p>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              The link works once and expires in an hour. Check your spam folder if it does not
              arrive.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Enter your email address and we will send you a link to choose a new password.
            </p>

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}
      </Card>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Link to="/login" className="font-medium text-emerald-700 hover:underline dark:text-emerald-400">
          Back to sign in
        </Link>
      </p>
    </main>
  );
}
